/**
 * CosmeticThumbnail3D — the 3D counterpart to {@link CosmeticPreview} for the
 * DENSE cosmetic tiles in the lantern shop, the season-pass tracks, the locker
 * item grids, and the Embers shop.
 *
 * THE GOAL (Wave 3): the premium 3D cosmetics the player sees on the locker
 * hero + in-game should be what they ALSO see while browsing — not a flatter 2D
 * SVG portrait. So instead of drawing the item on a flat `IconAvatar`, we lay
 * the candidate item onto a neutral BASE character ({@link composeIconWithCosmetic})
 * and show the SAME baked 3D thumbnail the {@link Character3DThumbnail} avatars
 * use — head-close for hats/faces/masks/accents, full head-to-feet for body
 * shapes and for border-aura / background-disc cosmetics whose look lives at the
 * figure's feet.
 *
 * PERFORMANCE (owner runs a low-end CPU): this rides the SHARED offscreen
 * renderer ({@link getCharacterThumbnail}) — ONE detached WebGL context that
 * bakes each (icon, frame) to a cached PNG exactly once, drained one render per
 * animation frame. A grid of 40 tiles is 40 plain `<img>`s sharing that one
 * context + an LRU image cache, NOT 40 live WebGL canvases. Because the base is
 * NEUTRAL (not the player's live icon), a given item's tile is byte-identical
 * across every surface and across equips, so the cache is hit hard and grids
 * stay static after the first bake.
 *
 * FALLBACK: until a thumbnail bakes (or forever, if WebGL is unavailable), the
 * supplied 2D `fallback` node shows — never an empty/black tile. Non-figure
 * cosmetics (titles, particles, seat-tile items) have no character figure, so
 * {@link CosmeticThumbnail3D} just renders the flat {@link CosmeticPreview} for
 * those and never touches the 3D path.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { CharacterIcon } from '@tt/types';
import { getCosmetic } from '@tt/lobby/cosmetics-catalog';
import { resolveCosmeticToInventory } from '@tt/lobby/cosmetic-resolver';
import { CosmeticPreview } from './CosmeticPreview';
import { composeIconWithCosmetic } from './cosmeticIcon';
import type { IconValue } from './IconAvatar';
import type { ThumbFrame } from './character3d-thumbnail';

type ThumbModule = typeof import('./character3d-thumbnail');

// Module-cached dynamic import so every tile shares ONE chunk load (the bundler
// de-dupes too, but memoising avoids re-creating the promise per mount). This is
// the SAME chunk Character3DThumbnail / Character3DHero pull, so opening a shop
// after the locker is already warm.
let modPromise: Promise<ThumbModule> | null = null;
function loadThumbModule(): Promise<ThumbModule> {
  if (!modPromise) modPromise = import('./character3d-thumbnail');
  return modPromise;
}

/** Neutral base the cosmetic is composed onto — mirrors CosmeticPreview's
 *  NEUTRAL_BASE so the 3D tile reads as "a plain character wearing this item". */
const NEUTRAL_BASE: CharacterIcon = {
  set: 'default',
  id: 0,
  hue: 220,
  body: 0,
  hat: 0,
  face: 0,
  accentHue: 40,
} as unknown as CharacterIcon;

/** Stable key for an icon's full appearance — mirrors @tt/shell-3d's iconHash
 *  fields. Local so this light component doesn't import the Three-laden shell. */
function iconKey(icon: CharacterIcon): string {
  const i = icon as IconValue;
  return [
    i.set, i.id, i.hue,
    i.body ?? -1, i.hat ?? -1, i.face ?? -1, i.accentHue ?? -1,
    i.paletteId ?? '', i.border ?? '', i.background ?? '', i.mask ?? '', i.outfit ?? '',
    (icon as CharacterIcon).particles ?? '', (icon as CharacterIcon).rimHue ?? -1,
  ].join('|');
}

interface Cosmetic3DThumbnailProps {
  /** The composed icon (a base character WEARING the item). */
  icon: CharacterIcon;
  /** Framing — `portrait` (head-close) or `figure` (full head-to-feet). */
  frame?: ThumbFrame;
  /** Square edge in CSS px. Default 56. */
  size?: number;
  /** Clip to a circle. Default true (matches the avatar tiles). */
  circular?: boolean;
  alt?: string | undefined;
  /** 2D node shown while the thumbnail bakes / if WebGL is unavailable. */
  fallback: ReactNode;
  className?: string | undefined;
}

/**
 * Low-level: bake + show the 3D thumbnail for an ALREADY-composed icon, holding
 * the `fallback` 2D node until the image is ready. Used directly by the Embers
 * shop (which composes from a {@link ShopItem}, not a catalog id).
 */
export function Cosmetic3DThumbnail({
  icon, frame = 'portrait', size = 56, circular = true, alt, fallback, className,
}: Cosmetic3DThumbnailProps) {
  const [url, setUrl] = useState<string | null>(null);
  const key = `${frame}|${iconKey(icon)}`;

  useEffect(() => {
    let alive = true;
    setUrl(null); // appearance changed — drop the stale image, show 2D meanwhile
    loadThumbModule()
      .then((m) => {
        const peeked = m.peekCharacterThumbnail(icon, frame);
        if (peeked) {
          if (alive) setUrl(peeked);
          return undefined;
        }
        return m.getCharacterThumbnail(icon, frame).then((u) => {
          if (alive && u) setUrl(u);
        });
      })
      .catch(() => {
        /* heavy chunk failed to load — keep the 2D fallback */
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!url) return <>{fallback}</>;

  return (
    <span
      className={className}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      style={{
        width: size,
        height: size,
        display: 'inline-block',
        borderRadius: circular ? '50%' : 12,
        overflow: 'hidden',
        flex: '0 0 auto',
        background:
          'radial-gradient(ellipse at 50% 38%, rgba(58,40,26,0.55) 0%, rgba(13,10,20,0.9) 100%)',
      }}
    >
      <img
        src={url}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </span>
  );
}

/** Pick the framing that best shows a given cosmetic kind in a small tile. */
function frameForKind(kind: string): ThumbFrame {
  switch (kind) {
    case 'border':
    case 'seat-border':
    case 'seat-bghue':
      // The RING/GLOW at the feet IS the product — aura-forward low framing
      // (full-figure deliberately tames the ring, which hid what's for sale;
      // owner 2026-07-05).
      return 'aura';
    case 'background':
      // Background backdrops live BEHIND the figure — a head portrait would
      // crop them out, so show the whole standing figure.
      return 'figure';
    case 'outfit':
      // Outfits dress the torso / arms / legs — a head portrait would crop
      // the clothing out, so show the whole standing figure.
      return 'figure';
    case 'particles':
    case 'seat-particle':
    case 'seat-background':
      // Ambient particles + seat backdrops surround the WHOLE figure —
      // full head-to-feet framing shows them.
      return 'figure';
    default:
      // hat / face / mask / accent / palette — head-close. NOTE body too: in
      // 3D the icon's body-shape IS rebuilt as the head silhouette (see
      // shell-3d figures.ts), so a head portrait shows a body cosmetic best.
      return 'portrait';
  }
}

interface CosmeticThumbnail3DProps {
  cosmeticId: string;
  /** Square edge in CSS px. Default 56. */
  size?: number;
  /** Clip to a circle. Default true (matches CosmeticPreview's PreviewBox). */
  circular?: boolean;
}

/**
 * Catalog-id wrapper used by the shop / season / locker grids: resolves the
 * cosmetic, composes it onto the neutral base, and shows the 3D thumbnail with
 * the {@link CosmeticPreview} as the loading/unavailable fallback. Non-figure
 * cosmetics (titles, particles, seat-tile items) have no figure representation,
 * so this renders the flat preview directly — exactly as before.
 */
export function CosmeticThumbnail3D({ cosmeticId, size = 56, circular = true }: CosmeticThumbnail3DProps) {
  const fallback = <CosmeticPreview cosmeticId={cosmeticId} size={size} />;
  const def = getCosmetic(cosmeticId);
  // Compose onto the neutral base; null ⇒ not a character-figure cosmetic
  // (seat-tile / title / particles) ⇒ keep the flat 2D preview.
  const composed = def ? composeIconWithCosmetic(NEUTRAL_BASE, cosmeticId) : null;
  if (!def || !composed) return fallback;
  const resolved = resolveCosmeticToInventory(cosmeticId);
  const frame = frameForKind(resolved?.kind ?? def.kind);
  return (
    <Cosmetic3DThumbnail
      icon={composed}
      frame={frame}
      size={size}
      circular={circular}
      alt={def.name}
      fallback={fallback}
    />
  );
}
